import { getActiveStoreId } from '../../../services/storeService';

const BASEURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  return {
    'Authorization': authToken ? `Bearer ${authToken}` : '',
    'Content-Type': 'application/json',
  };
};

// Helper function for FormData requests (without Content-Type header)
const getFormDataAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  return {
    'Authorization': authToken ? `Bearer ${authToken}` : ''
  };
};

export const fetchProducts = async (pageNumber = 1, pageSize = 10, inStock = null) => {
  try {
    let inStockValue = inStock === null || inStock.inStock === null? '' : inStock.inStock;
    let url = `${BASEURL}/Product/Pagination?Size=${pageSize}&PageNumber=${pageNumber}&inStock=${inStockValue}`;
    const storeId = await getActiveStoreId();
    if (storeId) url += `&BoutiqueId=${encodeURIComponent(storeId)}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch products:', error);
    throw error;
  }
};

export const fetchAllProducts = async () => {
  try {
    const response = await fetch(`${BASEURL}/Product/DropDown`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch products:', error);
    throw error;
  }
};


export const fetchProductsWithStock = async (pageNumber = 1, pageSize = 10 , isInStock) => {
  try {
    let url2 = `${BASEURL}/Product/Pagination?Size=${pageSize}&PageNumber=${pageNumber}&InStock=${isInStock}`;
    const storeId2 = await getActiveStoreId();
    if (storeId2) url2 += `&BoutiqueId=${encodeURIComponent(storeId2)}`;
    const response = await fetch(url2, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch products:', error);
    throw error;
  }
};


// Fetch pending return stock for a list of product ids (loaded separately to keep the list fast)
export const fetchProductsReturnStock = async (productIds = []) => {
  try {
    if (!productIds || productIds.length === 0) return [];
    const response = await fetch(`${BASEURL}/Product/PendingReturnStock`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(productIds)
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to fetch return stock:', error);
    return [];
  }
};



export const fetchProductsUsingFilter = async (filters = {}) => {
  try {
    const { 
      search, 
      inStock, 
      brand,
      brandId, // This is the ID we want to use
      marqueId, // Alternative name used in some components
      minPrice, 
      maxPrice, 
      page = 1, 
      pageSize = 10 ,
      category
    } = filters;
    
    // Build the URL manually to have more control over encoding
    let url = `${process.env.REACT_APP_API_URL}/product/pagination?pageNumber=${page}&size=${pageSize}`;
    const activeStoreId = await getActiveStoreId();
    if (activeStoreId) url += `&BoutiqueId=${encodeURIComponent(activeStoreId)}`;
    
    if (search) {
      url += `&searchTerms=${encodeURIComponent(search).replace(/\+/g, '%20')}`;
    }
    
    if (inStock === true) {
      url += '&inStock=true';
    } else if (inStock === false) {
      url += '&inStock=false';
    }
    

    if (category) {
      url += `&CategorieId=${encodeURIComponent(category)}`;
    }

    // Use marqueId or brandId - these should be the same thing with different naming
    const brandIdToUse = marqueId || brandId;
    
    if (brandIdToUse) {
      url += `&marqueId=${encodeURIComponent(brandIdToUse)}`;
    } else if (brand && brand !== 'Tous') {
      url += `&brand=${encodeURIComponent(brand)}`;
    }
    
    // Add price range filters
    if (minPrice !== undefined && minPrice !== null) {
      url += `&minPrice=${encodeURIComponent(minPrice)}`;
    }
    
    if (maxPrice !== undefined && maxPrice !== null) {
      url += `&maxPrice=${encodeURIComponent(maxPrice)}`;
    }
    
    console.log('Calling API:', url);
    
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Filter API response:', data);
    
    // Return the complete data structure
    return data;
  } catch (error) {
    console.error('Error fetching products with filter:', error);
    throw error;
  }
};

export const fetchProductById = async (productId) => {
  try {
    const response = await fetch(`${BASEURL}/Product/byId/${productId}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch product:', error);
    throw error;
  }
};

export const getAllReferences = async () => {
  try {
    const response = await fetch(`${BASEURL}/Product/GetAllReference`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch product:', error);
    throw error;
  }
};

export const deleteProduct = async (productId) => {
  try {
    const response = await fetch(`${BASEURL}/Product/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      return {
        status : response.status,
      };
    }
    
    return true;
  } catch (error) {
    console.error('Failed to delete product:', error);
    return error;
  }
};

export const deleteVariantImage = async (imagePath) => {
  try {
    const response = await fetch(`${BASEURL}/Product/ImageVariant`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ "imagePath": imagePath })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
    }
    
    return {
      success: true,
      message: "Image deleted successfully"
    };
  } catch (error) {
    console.error('Failed to delete variant image:', error);
    return {
      success: false,
      message: error.message
    };
  }
};


export const updateProduct = async (formData) => {
  try {
    const response = await fetch(`${BASEURL}/Product/updateproduct`, {
      method: 'PATCH',
      headers: getFormDataAuthHeaders(),
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
    }
    
    const result = await response.json();
    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('Failed to update product:', error);
    return {
      success: false,
      message: error.message
    };
  }
};


export const bulkDeleteProducts = async (productIds) => {
  try {
    const response = await fetch(`${BASEURL}/Product/BulkDelete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(productIds),
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return true;
  } catch (error) {
    console.error('Failed to bulk delete products:', error);
    throw error;
  }
};

export const deleteVariant = async (variandId) => {
  try {
    const response = await fetch(`${BASEURL}/Product/variant/${variandId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    return response;
  } catch (error) {
    console.error('Failed to bulk delete products:', error);
    throw error;
  }
};

// Add this function to products-controller.js
export const addProductVariants = async (productId, variants) => {
  try {
    const response = await fetch(`${BASEURL}/product/ajoutervariant`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(variants)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
    }
    
    const data = await response.json();
    
    return {
      success: true,
      message: "Variantes ajoutées avec succès",
      data
    };
  } catch (error) {
    console.error('Failed to add product variants:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// In products-controller.js
export const updateProductVariantStock = async (variantId, stock) => {
  try {
    const response = await fetch(`${BASEURL}/Product/updatestockvariant`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        "idProductVariant": variantId, // Changed from variantId to match expected format
        "stock": stock
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
    }
    
    return {
      success: true,
      message: "Stock mis à jour avec succès"
    };
  } catch (error) {
    console.error('Failed to update variant stock:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// In products-controller.js
export const addProductVariantImage = async (variantId, imageFile) => {
  try {
    // Create FormData object to handle file upload
    const formData = new FormData();
    formData.append('idProductVariant', variantId);
    formData.append('image', imageFile);
    
    const response = await fetch(`${BASEURL}/Product/Ajouterimagevariant`, {
      method: 'PATCH',
      headers: getFormDataAuthHeaders(),
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
    }
    
    // API returns the image URL directly as a string
    const imageUrl = await response.text();
    
    return {
      success: true,
      message: "Image de variante ajoutée avec succès",
      imageUrl: imageUrl // Direct URL from the API response
    };
  } catch (error) {
    console.error('Failed to add variant image:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Fetch total product count
export const fetchTotalProductCount = async () => {
  try {
    const response = await fetch(`${BASEURL}/Product/Pagination?Size=1&PageNumber=1`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();

    // Support multiple possible shapes returned by the API
    // Common shapes observed: { total: X }, { totalProduct: X }, { data: { total: X } }
    const totalCandidates = [
      data?.total,
      data?.totalProduct,
      data?.totalProducts,
      data?.data?.total,
      data?.data?.totalProduct,
      data?.data?.totalProducts
    ];

    for (const val of totalCandidates) {
      if (typeof val === 'number') return val;
    }

    // Fallback: if API returned pagination metadata differently, try to find a numeric property
    for (const key of Object.keys(data || {})) {
      if (typeof data[key] === 'number') return data[key];
    }

    return 0;
  } catch (error) {
    console.error('Failed to fetch total product count:', error);
    throw error;
  }
};